import type { ReactNode, JSX } from 'react'

import { Checkbox } from '../../components/Checkbox'
import type { CheckboxProps } from '../../components/Checkbox'
import { Form } from '../../components/Form'
import type { FormErrorProps } from '../../components/Form'
import { Input } from '../../components/Input'
import type { InputProps } from '../../components/Input'
import { Select } from '../../components/Select'
import type {
  SelectContentProps,
  SelectProps,
  SelectTriggerProps,
} from '../../components/Select'
import { Textarea } from '../../components/Textarea'
import type { TextareaProps } from '../../components/Textarea'
import { useFieldContext } from './contexts'
import { getFirstErrorMessage } from './getFirstErrorMessage'

type FieldPresentationProps = {
  label: ReactNode
  /** Helper text displayed when the field has no displayable error. */
  description?: ReactNode
  errorProps?: Omit<FormErrorProps, 'children'>
  required?: boolean
  fieldClassName?: string
}

type FieldMessageProps = Pick<
  FieldPresentationProps,
  'description' | 'errorProps'
> & {
  error: string | undefined
}

const FieldMessage = ({
  description,
  error,
  errorProps,
}: FieldMessageProps): JSX.Element =>
  error ? (
    <Form.Error {...errorProps}>{error}</Form.Error>
  ) : (
    <Form.Description>{description}</Form.Description>
  )

const getStringFieldValue = (value: unknown, componentName: string): string => {
  if (typeof value !== 'string') {
    throw new TypeError(`${componentName} requires a string-valued field.`)
  }

  return value
}

const getBooleanFieldValue = (
  value: unknown,
  componentName: string,
): boolean => {
  if (typeof value !== 'boolean') {
    throw new TypeError(`${componentName} requires a boolean-valued field.`)
  }

  return value
}

type TextInputType = 'email' | 'password' | 'search' | 'tel' | 'text' | 'url'

type TextFieldProps = FieldPresentationProps &
  Omit<
    InputProps,
    | 'aria-describedby'
    | 'aria-invalid'
    | 'aria-required'
    | 'defaultValue'
    | 'id'
    | 'name'
    | 'onBlur'
    | 'onChange'
    | 'required'
    | 'type'
    | 'value'
  > & {
    type?: TextInputType
  }

const TextField = ({
  description,
  errorProps,
  fieldClassName,
  label,
  required = false,
  ...inputProps
}: TextFieldProps): JSX.Element => {
  const field = useFieldContext<unknown>()
  const error = getFirstErrorMessage(field.state.meta.errors)
  const invalid = !field.state.meta.isValid
  const value = getStringFieldValue(field.state.value, 'TextField')

  return (
    <Form.Field
      className={fieldClassName}
      name={field.name}
      invalid={invalid}
      required={required}
    >
      <Form.Label>
        {label} <Form.RequiredIndicator />
      </Form.Label>
      <Form.Control>
        <Input
          {...inputProps}
          value={value}
          onBlur={field.handleBlur}
          onChange={(event) => field.handleChange(event.currentTarget.value)}
          required={required}
        />
      </Form.Control>
      <FieldMessage
        description={description}
        error={error}
        errorProps={errorProps}
      />
    </Form.Field>
  )
}

type TextareaFieldProps = FieldPresentationProps &
  Omit<
    TextareaProps,
    | 'aria-describedby'
    | 'aria-invalid'
    | 'aria-required'
    | 'defaultValue'
    | 'id'
    | 'name'
    | 'onBlur'
    | 'onChange'
    | 'required'
    | 'value'
  >

const TextareaField = ({
  description,
  errorProps,
  fieldClassName,
  label,
  required = false,
  ...textareaProps
}: TextareaFieldProps): JSX.Element => {
  const field = useFieldContext<unknown>()
  const error = getFirstErrorMessage(field.state.meta.errors)
  const invalid = !field.state.meta.isValid
  const value = getStringFieldValue(field.state.value, 'TextareaField')

  return (
    <Form.Field
      className={fieldClassName}
      name={field.name}
      invalid={invalid}
      required={required}
    >
      <Form.Label>
        {label} <Form.RequiredIndicator />
      </Form.Label>
      <Form.Control>
        <Textarea
          {...textareaProps}
          value={value}
          onBlur={field.handleBlur}
          onChange={(event) => field.handleChange(event.currentTarget.value)}
          required={required}
        />
      </Form.Control>
      <FieldMessage
        description={description}
        error={error}
        errorProps={errorProps}
      />
    </Form.Field>
  )
}

type CheckboxFieldProps = Omit<
  CheckboxProps,
  | 'aria-describedby'
  | 'aria-invalid'
  | 'aria-required'
  | 'checked'
  | 'children'
  | 'defaultChecked'
  | 'id'
  | 'name'
  | 'onBlur'
  | 'onChange'
  | 'required'
> &
  FieldPresentationProps

const CheckboxField = ({
  description,
  errorProps,
  fieldClassName,
  label,
  required = false,
  ...checkboxProps
}: CheckboxFieldProps): JSX.Element => {
  const field = useFieldContext<unknown>()
  const error = getFirstErrorMessage(field.state.meta.errors)
  const invalid = !field.state.meta.isValid
  const value = getBooleanFieldValue(field.state.value, 'CheckboxField')

  return (
    <Form.Field
      className={fieldClassName}
      name={field.name}
      invalid={invalid}
      required={required}
    >
      <Form.Control>
        <Checkbox
          {...checkboxProps}
          checked={value}
          onBlur={field.handleBlur}
          onChange={(checked) => field.handleChange(checked === true)}
          required={required}
        >
          {label} <Form.RequiredIndicator />
        </Checkbox>
      </Form.Control>
      <FieldMessage
        description={description}
        error={error}
        errorProps={errorProps}
      />
    </Form.Field>
  )
}

type SelectFieldProps = FieldPresentationProps &
  Omit<
    SelectProps,
    'children' | 'defaultValue' | 'name' | 'onValueChange' | 'value'
  > & {
    children: ReactNode
    placeholder?: string
    triggerProps?: Omit<
      SelectTriggerProps,
      | 'aria-describedby'
      | 'aria-invalid'
      | 'aria-required'
      | 'children'
      | 'disabled'
      | 'id'
      | 'name'
      | 'onBlur'
      | 'placeholder'
      | 'type'
    >
    contentProps?: Omit<SelectContentProps, 'children'>
  }

const SelectField = ({
  children,
  contentProps,
  description,
  errorProps,
  fieldClassName,
  label,
  placeholder,
  required = false,
  triggerProps,
  ...selectProps
}: SelectFieldProps): JSX.Element => {
  const field = useFieldContext<unknown>()
  const error = getFirstErrorMessage(field.state.meta.errors)
  const invalid = !field.state.meta.isValid
  const value = getStringFieldValue(field.state.value, 'SelectField')

  return (
    <Form.Field
      className={fieldClassName}
      name={field.name}
      invalid={invalid}
      required={required}
    >
      <Form.Label>
        {label} <Form.RequiredIndicator />
      </Form.Label>
      <Select
        {...selectProps}
        name={field.name}
        required={required}
        value={value}
        onValueChange={field.handleChange}
      >
        <Form.Control>
          <Select.Trigger
            {...triggerProps}
            placeholder={placeholder}
            onBlur={field.handleBlur}
          />
        </Form.Control>
        <Select.Content {...contentProps}>{children}</Select.Content>
      </Select>
      <FieldMessage
        description={description}
        error={error}
        errorProps={errorProps}
      />
    </Form.Field>
  )
}

export {
  CheckboxField,
  getBooleanFieldValue,
  getStringFieldValue,
  SelectField,
  TextareaField,
  TextField,
}
export type {
  CheckboxFieldProps,
  SelectFieldProps,
  TextareaFieldProps,
  TextFieldProps,
}
